<script setup lang="ts">
import NumericKeypad from "@innoclouds/ui/primitives/numericKeypad/NumericKeypad.vue";
import { useTranslation } from "i18next-vue";
import { computed, ref, watch } from "vue";
import { useDecorationStore } from "#/store/decoration";
import { formatPrice } from "#/utils/i18next";

interface Props {
	maxWithdraw: number;
	applied: number;
	allowDecimal?: boolean;
	bonusSign?: string;
	decoration?: string;
}

const props = withDefaults(defineProps<Props>(), {
	allowDecimal: false,
	bonusSign: "",
	decoration: undefined,
});

const emit = defineEmits<{
	apply: [value: number];
	close: [];
}>();

const { t } = useTranslation();
const decor = useDecorationStore().screen(props.decoration);

const DECIMAL_SEPARATOR = ",";
const MAX_DIGITS = 9;

const draft = ref("");
const isClampedToLimit = ref(false);

const toDraft = (value: number) => {
	const rounded = Math.round(value * 100) / 100;
	return rounded > 0 ? String(rounded).replace(".", DECIMAL_SEPARATOR) : "";
};

const parse = (value: string) => {
	const parsedValue = Number.parseFloat(value.replace(DECIMAL_SEPARATOR, "."));
	if (!Number.isFinite(parsedValue) || parsedValue <= 0) return 0;
	return props.allowDecimal ? Math.round(parsedValue * 100) / 100 : Math.floor(parsedValue);
};

const setDraft = (value: string) => {
	if (parse(value) > props.maxWithdraw) {
		draft.value = toDraft(props.maxWithdraw);
		isClampedToLimit.value = true;
		return;
	}
	draft.value = value;
	isClampedToLimit.value = false;
};

watch(
	() => props.applied,
	(value) => {
		draft.value = toDraft(value);
		isClampedToLimit.value = false;
	},
	{ immediate: true },
);

const parsed = computed(() => parse(draft.value));
const clamped = computed(() => Math.min(parsed.value, Math.max(props.maxWithdraw, 0)));
const canApply = computed(() => props.maxWithdraw > 0);

const appendDigit = (digit: string) => {
	const [, fraction] = draft.value.split(DECIMAL_SEPARATOR);
	if (fraction !== undefined && fraction.length >= 2) return;
	if (draft.value.replace(DECIMAL_SEPARATOR, "").length >= MAX_DIGITS) return;

	setDraft(draft.value === "0" ? digit : draft.value + digit);
};

const appendSeparator = () => {
	if (!props.allowDecimal || draft.value.includes(DECIMAL_SEPARATOR)) return;
	setDraft(`${draft.value || "0"}${DECIMAL_SEPARATOR}`);
};

const backspace = () => {
	setDraft(draft.value.slice(0, -1));
};

const clear = () => {
	setDraft("");
};

const withdrawAll = () => {
	draft.value = toDraft(props.maxWithdraw);
	isClampedToLimit.value = false;
};

const submit = () => {
	emit("apply", clamped.value);
	emit("close");
};
</script>

<template>
	<div class="kso_bonuses" v-bind="decor.bind('kso_bonuses')">
		<p v-if="!canApply" class="kso_bonuses_notice kso_bonuses_notice_muted" v-bind="decor.bind('kso_bonuses_notice', { muted: true })">
			{{ t(`cart.footer.bonus.balance.empty`) }}
		</p>

		<template v-else>
			<div class="kso_bonuses_controls" v-bind="decor.bind('kso_bonuses_controls')">
				<div class="kso_bonuses_field" :data-invalid="isClampedToLimit" v-bind="decor.bind('kso_bonuses_field', { invalid: isClampedToLimit })">
					<span v-if="!draft.length" class="kso_bonuses_placeholder" v-bind="decor.bind('kso_bonuses_placeholder')"
						>{{ t(`kso.bonuses.input.placeholder`) }}</span
					>
					<span v-else class="kso_bonuses_draft" v-bind="decor.bind('kso_bonuses_draft')">{{ draft }}</span>
					<span v-if="draft.length" class="kso_bonuses_caret" aria-hidden="true" v-bind="decor.bind('kso_bonuses_caret')" />
				</div>
				<button type="button" class="kso_bonuses_btn kso_bonuses_btn_ghost" v-bind="decor.bind('kso_bonuses_all')" @click="withdrawAll">
					{{ t(`kso.bonuses.button.all`) }}
				</button>
				<button type="button" class="kso_bonuses_btn kso_bonuses_btn_primary" v-bind="decor.bind('kso_bonuses_ok')" @click="submit">
					{{ t(`kso.bonuses.button.confirm`) }}
				</button>
			</div>

			<p v-if="isClampedToLimit" class="kso_bonuses_notice" v-bind="decor.bind('kso_bonuses_notice')">
				{{ t(`kso.bonuses.limit`, { count: formatPrice(props.maxWithdraw, props.bonusSign) }) }}
			</p>

			<NumericKeypad
				class="kso_bonuses_keypad"
				:key-attrs="decor.bind('kso_key')"
				v-bind="decor.bind('kso_keypad')"
				:with-decimal="props.allowDecimal"
				:decimal-separator="DECIMAL_SEPARATOR"
				@digit="appendDigit"
				@separator="appendSeparator"
				@clear="clear"
				@backspace="backspace"
			/>
		</template>
	</div>
</template>

<style lang="scss" scoped>
  @use './ksoBonusesPad.scss';
</style>
